import { useEffect, useRef } from "react";
import { useOrderRealtime } from "@utils/store/realtime/useOrderRealtime";
import type { OrderRealtimeEvent } from "./orderRealtimeSession";

/**
 * 頁面端：接收訂單即時事件與 resume（連線由 PageWrapper 的 useOrderRealtimeConnection 管）。
 *
 * 為什麼用 store.subscribe 而不是「render 時用 selector 讀 eventSeq，再用 useEffect 看它變了沒」：
 * React 18 會把短時間內的多次 store 更新合併成一次 render，連續兩個事件只會看到最後一個，
 * 前一個（可能是另一張單）就被吃掉了。subscribe 的 listener 是每次 set 都同步呼叫，一個不漏。
 */
export function useOrderRealtimeListener({
  onEvent,
  onResume,
}: {
  onEvent?: (e: OrderRealtimeEvent) => void;
  onResume?: () => void;
}): void {
  // 用 ref 保存最新的 callback：callback 每次 render 都是新的，不能讓它讓 subscribe 重掛
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  useEffect(
    () =>
      useOrderRealtime.subscribe((state, prev) => {
        if (state.eventSeq !== prev.eventSeq && state.lastEvent) onEventRef.current?.(state.lastEvent);
        if (state.resumeSeq !== prev.resumeSeq) onResumeRef.current?.();
      }),
    [],
  );
}
